import { nanoid } from 'nanoid'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { createGame, findPendingGames, getGame, joinGame, subscribeToMyGames } from 'features/firebase/game-operations'
import { useAuthenticatedUser } from 'features/user/auth-store'

import { GreenButton } from 'shared/components'
import { Card } from 'shared/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTrigger } from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/components/ui/select'

const defaultGameTime = 900 * 1000

export const Play: FC = () => {
  const { user, player } = useAuthenticatedUser()
  const [myGames, setMyGames] = useState<string[]>([])
  const navigate = useNavigate()
  const [gameTime, setGameTime] = useState(defaultGameTime)
  const [gameById, setGameById] = useState('')

  const joinExistingGame = async (gameId: string) => {
    if (myGames.includes(gameId)) {
      navigate(`/play/${gameId}`)
    } else {
      const game = await getGame(gameId)
      if (game) {
        await joinGame(gameId, user.uid, player.rating)
        navigate(`/play/${gameId}`)
      } else {
        toast.error('Game not found')
      }
    }
  }

  const playOnline = async () => {
    const rawPendingGames = await findPendingGames()
    const pendingGames = rawPendingGames.filter(game => !myGames.includes(game.gameId) && gameTime === game.timeToPlay)

    if (pendingGames.length) {
      const game = pendingGames[0]
      joinExistingGame(game.gameId)
    } else {
      createNewGame(false, true)
    }
  }

  const createNewGame = async (withAI: boolean, autoAddAI: boolean) => {
    const newGameId = nanoid(6)
    await createGame(newGameId, user.uid, player.rating, gameTime, withAI, autoAddAI)
    navigate(`/play/${newGameId}`)
  }

  useEffect(() => {
    const unsubscribe = subscribeToMyGames(user.uid, games => {
      if (games) {
        setMyGames(Object.keys(games))
      }
    })

    return unsubscribe
  }, [user.uid])

  return (
    <Card className="mx-auto my-10 flex w-11/12 flex-col items-center justify-center p-5 lg:w-[800px]">
      <Select defaultValue={defaultGameTime.toString()} onValueChange={val => setGameTime(parseInt(val))}>
        <div className="flex items-center gap-5">
          <Label className="text-xl">Game time</Label>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
        </div>
        <SelectContent>
          <SelectItem value="180000">3 minutes</SelectItem>
          <SelectItem value="300000">5 minutes</SelectItem>
          <SelectItem value="600000">10 minutes</SelectItem>
          <SelectItem value="900000">15 minutes</SelectItem>
          <SelectItem value="1200000">20 minutes</SelectItem>
          <SelectItem value="1800000">30 minutes</SelectItem>
          <SelectItem value="3600000">60 minutes</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex flex-col items-center gap-3 p-10">
        <GreenButton className="w-full sm:w-[300px]" onClick={playOnline} text="Play online" />
        <Dialog>
          <DialogTrigger asChild>
            <GreenButton className="w-full sm:w-[300px]" text="Play with a Friend" />
          </DialogTrigger>
          <DialogContent className="w-11/12 max-w-[850px] rounded-xl p-5">
            <DialogHeader>Paste Game ID below or create a new Game</DialogHeader>
            <DialogDescription>Game ID is located in top right corner of the game</DialogDescription>
            <Input value={gameById} onChange={e => setGameById(e.target.value)} placeholder="Game ID" />
            <GreenButton className="w-full" onClick={() => joinExistingGame(gameById)} text="Join" />
            <GreenButton className="w-full" onClick={() => createNewGame(false, false)} text="Create Game" />
          </DialogContent>
        </Dialog>
        <GreenButton className="w-full sm:w-[300px]" onClick={() => createNewGame(true, false)} text="Play vs AI" />
      </div>
    </Card>
  )
}
